// Übersicht des Mieterportals (Screen mieter/01). Nur Widgets mit echten Daten (Entscheidung
// 22.10.2026): Meine Wohnung, Schaden melden, Meine Meldungen, Miete, Ansprechpartner.
// „Nächster Termin“ nur, wenn es einen gibt; Müll/Notdienst fehlen, bis es Daten dazu gibt.
import { AddressBook, CalendarBlank, CaretRight, Info, ChatCircleDots, EnvelopeSimple, FileText, Key, Plus, Wallet, Wrench } from '@phosphor-icons/react';
import { GlasWidget, IconChip } from '../../components/portal/bausteine';
import {
  euro, euroGanz, kategorieAusTitel, meldungStufe, mieterSeit, naechsteFaelligkeit, warmmiete, wohnungUntertitel, zahlText,
  type Meldung, type MieterKontext,
} from '../../lib/portal/mieter';
import { StufenBalken, StufenChip } from './MieterMeldungen';
import { anfrageBeantwortet, anfrageStatusText } from '../../lib/portal/einsaetze';
import { KATEGORIE_STIL } from './symbole';
import { terminBlatt, terminZeit, type PortalTermin } from '../../lib/portal/mieterE5';
import { terminFarbe } from './MieterTermine';

const LINK_KNOPF = { alignSelf: 'flex-start', height: 34, padding: '0 4px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 5 } as const;

export default function MieterUebersicht({ kontext, meldungen, termine, jetzt, onMelden, onAnfrage, onTab }: {
  kontext: MieterKontext;
  meldungen: Meldung[];
  termine: PortalTermin[];
  ort: string;
  jetzt: Date;
  onMelden: () => void;
  /** #564: Frage an die Verwaltung */
  onAnfrage?: () => void;
  onTab: (t: 'meldungen' | 'dokumente' | 'mietkonto' | 'termine') => void;
}) {
  const naechster = termine[0] ?? null;
  const m = kontext.mieter;
  const heute = jetzt.toISOString().slice(0, 10);
  const miete = warmmiete(m, heute);
  const faellig = naechsteFaelligkeit(m.miete_faellig_tag, jetzt);
  const offen = meldungen.filter((x) => meldungStufe(x) < 3);
  const seit = mieterSeit(m.anrede, m.mietbeginn);
  const strasse = [kontext.objekt?.strasse, kontext.objekt?.hausnummer].filter(Boolean).join(' ');
  const ap = kontext.ansprechpartner?.name ? kontext.ansprechpartner : null;
  const fakten: [string, string][] = [];
  const flaeche = zahlText(kontext.einheit?.wohnflaeche);
  if (flaeche) fakten.push(['Wohnfläche', `${flaeche} m²`]);
  const zimmer = zahlText(kontext.einheit?.zimmer);
  if (zimmer) fakten.push(['Zimmer', zimmer]);
  if (miete > 0) fakten.push(['Warmmiete', euroGanz(miete)]);
  if (m.kaution) fakten.push(['Kaution', euroGanz(Number(m.kaution))]);
  const vorname = m.vorname?.trim();
  const initialen = (ap?.name ?? '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');

  return (
    <div className="p-rein" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '6px 4px 0' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--p-w45)' }}>{jetzt.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
        <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em' }}>{vorname ? `Hallo ${vorname}` : 'Hallo'}</h1>
      </div>

      <div className="mp-raster">
        {/* w: Meine Wohnung */}
        <GlasWidget farbe="wohnung" icon={Key} titel="Meine Wohnung" className="mp-w" style={{ padding: 18, gap: 16 }}
          rechts={seit ? <span style={{ fontSize: 11.5, fontWeight: 800, padding: '5px 10px', borderRadius: 12, background: 'var(--p-gold-fl)', color: 'var(--p-gold-text)', whiteSpace: 'nowrap' }}>{seit}</span> : undefined}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1, overflowWrap: 'anywhere' }}>{strasse || 'Ihre Wohnung'}</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--p-w55)' }}>{wohnungUntertitel(kontext)}</span>
          </div>
          {fakten.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
              {fakten.map(([l, v]) => (
                <div key={l} style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '12px 14px', borderRadius: 16, background: 'var(--p-w05)', boxShadow: 'inset 0 0 0 0.5px var(--p-w06)' }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--p-w45)' }}>{l}</span>
                  <span className="p-zahl" style={{ fontSize: 19, fontWeight: 800 }}>{v}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="p-knopf" onClick={() => onTab('dokumente')} data-tip="Mietvertrag und weitere Unterlagen öffnen" style={{ height: 40, border: 'none', background: 'var(--p-w08)', fontSize: 13 }}>
              <FileText size={17} /> Mietvertrag
            </button>
          </div>
        </GlasWidget>

        {/* s: Schaden melden */}
        <GlasWidget farbe="heizung" icon={Wrench} titel="Schaden melden" className="mp-s">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.015em' }}>Etwas kaputt?</span>
            <span style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.45, color: 'var(--p-w60)' }}>Melden Sie Schäden hier. Unter „Meldungen“ sehen Sie jederzeit, wie weit wir sind.</span>
          </div>
          <div style={{ flex: 1 }} />
          <button type="button" className="p-knopf p-knopf-gold" onClick={onMelden} data-tip="Formular zum Melden eines Schadens öffnen" style={{ height: 46 }}>
            <Plus size={16} weight="bold" /> Neue Meldung
          </button>
          {onAnfrage && (
            <button type="button" onClick={onAnfrage} data-tip="Eine Frage an die Verwaltung schicken – die Antwort kommt hier im Portal"
              style={{ ...LINK_KNOPF, alignSelf: 'center', color: 'var(--p-lila-link)' }}>
              Oder eine Frage an die Verwaltung <CaretRight size={13} weight="bold" />
            </button>
          )}
        </GlasWidget>

        {/* t: Nächster Termin (nur wenn es einen gibt) */}
        {naechster && (
          <GlasWidget farbe={terminFarbe(naechster)} icon={CalendarBlank} titel="Nächster Termin">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ width: 54, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '6px 0', borderRadius: 14, background: 'var(--p-w08)' }}>
                <span style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--c1)', textTransform: 'uppercase' }}>{terminBlatt(naechster.start_datum).monat}</span>
                <span style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1 }}>{terminBlatt(naechster.start_datum).tag}</span>
              </span>
              <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 15.5, fontWeight: 800, overflowWrap: 'anywhere' }}>{naechster.titel}</span>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--p-w55)' }}>
                  {new Date(naechster.start_datum).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })} · {terminZeit(naechster)}
                </span>
                {naechster.firma && <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--p-w55)' }}>{naechster.firma}</span>}
              </span>
            </div>
            {naechster.hinweis && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 14, background: 'rgba(var(--rgb),0.12)', fontSize: 12.5, fontWeight: 600, lineHeight: 1.4, color: 'var(--p-w85)' }}>
                <Info size={15} weight="fill" style={{ marginTop: 1, color: 'var(--c1)' }} />{naechster.hinweis}
              </div>
            )}
            <div style={{ flex: 1 }} />
            <button type="button" onClick={() => onTab('termine')} data-tip="Alle anstehenden Termine" style={{ ...LINK_KNOPF, color: 'var(--p-blau-link)' }}>
              Alle Termine <CaretRight size={13} weight="bold" />
            </button>
          </GlasWidget>
        )}

        {/* m: Meine Meldungen */}
        <GlasWidget farbe="gemein" icon={ChatCircleDots} titel="Meine Meldungen" 
          rechts={<span style={{ fontSize: 12, fontWeight: 800, color: 'var(--p-w50)' }}>{offen.length} offen</span>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {offen.slice(0, 2).map((x) => {
              const stufe = meldungStufe(x);
              const stil = KATEGORIE_STIL[kategorieAusTitel(x.titel)];
              return (
                <button key={x.id} type="button" className="p-zeile" onClick={() => onTab('meldungen')} data-tip="Meldung ansehen" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8, padding: '10px 12px' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <stil.icon size={16} weight="fill" className={`p-farbe-${stil.farbe}`} style={{ color: 'var(--c1)' }} />
                    <span className="p-kuerzen" style={{ flex: 1, fontSize: 13.5, fontWeight: 800 }}>{x.titel}</span>
                    {x.art === 'anfrage' ? (
                      <span style={{ fontSize: 11, fontWeight: 800, padding: '4px 9px', borderRadius: 10, whiteSpace: 'nowrap', flexShrink: 0,
                        background: anfrageBeantwortet(x) ? 'var(--p-ton-ok-bg)' : 'var(--p-w08)', color: anfrageBeantwortet(x) ? 'var(--p-ton-ok-fg)' : 'var(--p-w75)' }}>
                        {anfrageStatusText(x)}
                      </span>
                    ) : <StufenChip stufe={stufe} />}
                  </span>
                  {x.art !== 'anfrage' && <StufenBalken stufe={stufe} />}
                </button>
              );
            })}
            {offen.length === 0 && <span style={{ padding: '10px 4px', fontSize: 13.5, fontWeight: 600, color: 'var(--p-w50)' }}>Keine offenen Meldungen.</span>}
          </div>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={() => onTab('meldungen')} data-tip="Alle eigenen Meldungen mit Status" style={{ ...LINK_KNOPF, color: 'var(--p-lila-link)' }}>
            Alle Meldungen <CaretRight size={13} weight="bold" />
          </button>
        </GlasWidget>

        {/* k: Miete (Kontostand folgt mit der Buchhaltung) */}
        <button type="button" onClick={() => onTab('mietkonto')} data-tip="Mietkonto öffnen" className="p-glas p-hebt p-farbe-konto"
          style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'left', color: 'var(--p-w)', border: 'none', cursor: 'pointer' }}>
          <div className="p-glas-schimmer"><Wallet size={170} weight="duotone" className="p-wasserzeichen" style={{ opacity: 0.12 }} /></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
            <IconChip farbe="konto" icon={Wallet} />
            <span style={{ fontSize: 14, fontWeight: 800, flex: 1 }}>Mietkonto</span>
            <CaretRight size={14} weight="bold" style={{ color: 'var(--p-w35)' }} />
          </div>
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            <span className="p-zahl" style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.015em' }}>{euro(miete)}</span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--p-w55)' }}>monatliche Miete</span>
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--p-w60)' }}>
            Nächste Miete: <span style={{ color: 'var(--p-w)' }}>{euro(miete)}</span> am {faellig.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
          </span>
        </button>

        {/* a: Ansprechpartner (nur wenn das Objekt einen Zuständigen hat) */}
        {ap && (
          <GlasWidget farbe="kontakt" icon={AddressBook} titel="Ihre Ansprechpartner">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 16, background: 'var(--p-w05)' }}>
              <span aria-hidden style={{ width: 40, height: 40, borderRadius: 13, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(160deg, var(--p-gold-text-hell), var(--p-gold-text))', color: 'var(--p-gold-ink)', fontSize: 13, fontWeight: 800 }}>{initialen}</span>
              <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <span className="p-kuerzen" style={{ fontSize: 13.5, fontWeight: 800 }}>{ap.name}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--p-w50)' }}>Verwaltung</span>
              </span>
              {ap.email && (
                <a href={`mailto:${ap.email}`} data-tip={`E-Mail an ${ap.name}`} aria-label={`E-Mail an ${ap.name}`}
                  style={{ width: 36, height: 36, borderRadius: 18, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--p-mail-bg)', color: 'var(--p-mail-ink)' }}>
                  <EnvelopeSimple size={16} weight="fill" />
                </a>
              )}
            </div>
          </GlasWidget>
        )}
      </div>
    </div>
  );
}
